'use client';

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import PropTypes from 'prop-types';
import Icon from '@/components/ui/AppIcon';
import { visibleTiers, resolveTierPrice, tierDiscountPercent, pricePerTicketFor } from '@/lib/pricing';

const ManualNumberSelector = ({
  totalTickets,
  selectedNumbers = [],
  userSelectedNumbers = [],
  userPurchasedNumbers = [],
  othersPurchasedNumbers = [],
  reservedNumbers = [],
  userReservedNumbers = [],
  onNumberSelect,
  onNumberDeselect,
  onMultipleNumberSelect,
  onMultipleNumberDeselect,
  // Optional — surface bulk-pricing info on the numbers tab so admins
  // see the same discount nudges they get on the order summary.
  ticketPrice = 0,
  bulkPricingTiers = []
}) => {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchNumber, setSearchNumber] = useState('');
  const [visuallySelectedNumbers, setVisuallySelectedNumbers] = useState(new Set());
  
  // NEW: Add expiration countdown state
  const [expirationCountdowns, setExpirationCountdowns] = useState(new Map());

  const numbersPerPage = 100;
  const totalPages = Math.ceil(totalTickets / numbersPerPage);

  const startNumber = (currentPage - 1) * numbersPerPage + 1;
  const endNumber = Math.min(currentPage * numbersPerPage, totalTickets);

  useEffect(() => {
    const allUserNumbers = new Set([
      ...userSelectedNumbers,
      ...userReservedNumbers?.map(res => res?.number)
    ]);
    setVisuallySelectedNumbers(allUserNumbers);
  }, [userSelectedNumbers, userReservedNumbers]);

  // NEW: Track expiration times for visual countdown
  useEffect(() => {
    const newCountdowns = new Map();
    
    // Track user's reserved numbers expiration
    userReservedNumbers?.forEach(res => {
      if (res?.expiresAt) {
        newCountdowns?.set(res?.number, res?.expiresAt);
      }
    });
    
    // Track other users' reserved numbers expiration. These are always other
    // players' holds (no player ids are sent), so guests see the time too.
    reservedNumbers?.forEach(res => {
      if (res?.expiresAt) {
        newCountdowns?.set(res?.number, res?.expiresAt);
      }
    });
    
    setExpirationCountdowns(newCountdowns);

    // Update countdown every second
    const intervalId = setInterval(() => {
      setExpirationCountdowns(prev => new Map(prev));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [userReservedNumbers, reservedNumbers, user?.id]);

  const handleNumberClick = (number) => {
    const isUserSelected = userSelectedNumbers?.includes(number);
    const isUserReserved = userReservedNumbers?.some(res => res?.number === number);
    
    if (isUserSelected || isUserReserved) {
      onNumberDeselect?.(number);
      setVisuallySelectedNumbers(prev => {
        const newSet = new Set(prev);
        newSet?.delete(number);
        return newSet;
      });
    } else {
      onNumberSelect?.(number);
      setVisuallySelectedNumbers(prev => new Set([...prev, number]));
    }
  };

  const handleClearAllUserNumbers = () => {
    const allUserNumbers = [
      ...userSelectedNumbers,
      ...userReservedNumbers?.map(res => res?.number)
    ];
    if (allUserNumbers?.length > 0) {
      onMultipleNumberDeselect?.(allUserNumbers);
    }
  };

  // Quick Pick — pick N random *currently available* numbers across the
  // whole competition and reserve them in a single batch. Uses the same
  // onMultipleNumberSelect path the lucky-dip mode uses, so all the
  // existing race + cap protections apply.
  const [quickPickCount, setQuickPickCount] = useState(5);
  const [quickPickBusy, setQuickPickBusy] = useState(false);

  const handleQuickPick = async () => {
    if (quickPickBusy) return;
    const desired = Math.max(1, Math.min(Number(quickPickCount) || 0, 50));

    // Build the available pool: not purchased (by anyone), not reserved
    // (by anyone), not already in the user's selections.
    const taken = new Set([
      ...(userPurchasedNumbers || []),
      ...(othersPurchasedNumbers || []),
      ...(userSelectedNumbers || []),
      ...((userReservedNumbers || []).map(r => r?.number)),
      ...((reservedNumbers || []).map(r => r?.number)),
    ]);

    const pool = [];
    for (let n = 1; n <= totalTickets; n++) {
      if (!taken.has(n)) pool.push(n);
    }

    if (pool.length === 0) {
      alert('No tickets available — every number has been reserved or sold.');
      return;
    }

    // Fisher–Yates shuffle, then take the first N.
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    const picked = pool.slice(0, Math.min(desired, pool.length));

    try {
      setQuickPickBusy(true);
      await onMultipleNumberSelect?.(picked);
      setVisuallySelectedNumbers(prev => {
        const next = new Set(prev);
        picked.forEach(n => next.add(n));
        return next;
      });
    } finally {
      setQuickPickBusy(false);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Sets, because the sold list can run to thousands of numbers.
  const userPurchasedSet = useMemo(() => new Set(userPurchasedNumbers || []), [userPurchasedNumbers]);
  const othersPurchasedSet = useMemo(() => new Set(othersPurchasedNumbers || []), [othersPurchasedNumbers]);

  const getNumberState = (number) => {
    const isSelectedByUser = userSelectedNumbers?.includes(number);
    const isReservedByUser = userReservedNumbers?.some(res => res?.number === number);
    const isVisuallySelected = visuallySelectedNumbers?.has(number);

    // NEW: Check if ticket is purchased by current user (BLUE)
    const isPurchasedByUser = userPurchasedSet.has(number);

    // NEW: Check if ticket is purchased by other users (RED)
    const isPurchasedByOthers = othersPurchasedSet.has(number);
    
    if (isPurchasedByUser) return 'purchased-user';
    if (isPurchasedByOthers) return 'purchased-others';
    if (isSelectedByUser) return 'selected-user';
    if (isReservedByUser) return 'reserved-user';
    if (isVisuallySelected) return 'reserved-user';
    
    // --------------------------------------------------------------
    // FIXED BLOCK (replaces selected-others logic)
    // --------------------------------------------------------------
    const isReservedByOthers = reservedNumbers?.some(res =>
      res?.number === number && (!user || res?.userId !== user?.id)
    );

    if (isReservedByOthers) return 'reserved-others';

    return 'available';
  };

  const getNumberClassName = (state) => {
    const baseClass = 'w-12 h-12 rounded-[6px] text-sm font-medium transition-all duration-200 border-2 cursor-pointer hover:scale-105';
    
    switch (state) {
      case 'purchased-user':
        return `${baseClass} bg-blue-600 text-white border-blue-700 shadow-lg ring-2 ring-blue-400`;
      case 'purchased-others':
        return `${baseClass} bg-red-600 text-white border-red-700 cursor-not-allowed opacity-90`;
      case 'selected-user':
        return `${baseClass} bg-blue-500 text-white border-blue-600 shadow-lg ring-2 ring-blue-300`;
      case 'reserved-user':
        return `${baseClass} bg-yellow-400 text-yellow-900 border-yellow-500 shadow-md ring-2 ring-yellow-300`;
      case 'reserved-others':
        return `${baseClass} bg-orange-400 text-orange-900 border-orange-500 cursor-not-allowed opacity-75`;
      default:
        return `${baseClass} bg-transparent text-foreground border-border hover:border-blue-400 hover:text-blue-600`;
    }
  };

  const getRemainingTime = (number) => {
    const expiresAt = expirationCountdowns?.get(number);
    if (!expiresAt) return null;
    
    const remaining = Math.max(0, Math.floor((new Date(expiresAt)?.getTime() - Date.now()) / 1000));
    return remaining;
  };

  const formatRemainingTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    const number = parseInt(searchNumber);
    if (number >= 1 && number <= totalTickets) {
      const targetPage = Math.ceil(number / numbersPerPage);
      goToPage(targetPage);
      setSearchNumber('');
    }
  };

  // Previous / Next, shown above and below the grid.
  const renderPager = (where) =>
    totalPages > 1 && (
      <nav
        aria-label={`Number pages (${where})`}
        className="flex items-center justify-between gap-3"
      >
        <button
          type="button"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="min-h-[44px] min-w-[44px] px-4 border border-border rounded-[6px] text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
        >
          Previous
        </button>
        <span className="text-sm text-muted-foreground text-center tabular-nums">
          Page {currentPage} of {totalPages}
          <span className="block">Numbers {startNumber}–{endNumber}</span>
        </span>
        <button
          type="button"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="min-h-[44px] min-w-[44px] px-4 border border-border rounded-[6px] text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
        >
          Next
        </button>
      </nav>
    );

  const renderNumbers = () => {
    const numbers = [];
    for (let i = startNumber; i <= endNumber; i++) {
      const state = getNumberState(i);
      let isDisabled = state === 'reserved-others' || 
                       state === 'purchased-user' || 
                       state === 'purchased-others';
      const remainingTime = getRemainingTime(i);
      const showCountdown = remainingTime !== null && remainingTime > 0 && 
                          (state === 'reserved-user' || state === 'reserved-others');
      
      // UPDATED: More explicit data-state mapping with guaranteed value
      const getDataState = () => {
        if (state === 'purchased-user' || state === 'purchased-others') return 'taken';
        if (state === 'selected-user' || state === 'reserved-user') return 'selected';
        return 'available';
      };
      
      const dataState = getDataState();
      
      // Ensure aria-pressed is true when selected
      const isAriaPressed = state === 'selected-user' || state === 'reserved-user' || state === 'purchased-user';
      
      numbers?.push(
        <button
          key={i}
          data-testid="ticket"
          data-ticket-number={i}
          data-state={dataState}
          onClick={() => handleNumberClick(i)}
          disabled={isDisabled}
          className={`${getNumberClassName(state)} relative group`}
          aria-pressed={isAriaPressed}
          aria-label={`Number ${i} - ${
            state === 'purchased-user' ? 'Purchased by you' :
            state === 'purchased-others' ? 'Purchased by another user' :
            state === 'reserved-user' ? 'Selected by you' :
            state === 'reserved-others' ? 'Reserved by another user' :
            state === 'selected-user'? 'Confirmed selection' : 'Available'
          }`}
          title={
            state === 'purchased-user' ? 'You purchased this ticket' :
            state === 'purchased-others' ? 'Already purchased by another user' :
            state === 'reserved-user' ? `Selected by you - ${formatRemainingTime(remainingTime)} remaining` :
            state === 'reserved-others' ? `Reserved by another user - ${formatRemainingTime(remainingTime)} remaining` :
            state === 'selected-user'? 'Confirmed selection - Click to remove' : 'Click to select'
          }
        >
          <span className="relative z-10">{i}</span>
          {showCountdown && (
            <span className="absolute inset-x-0 bottom-0 text-[9px] font-bold text-center leading-none pb-0.5 z-20">
              {formatRemainingTime(remainingTime)}
            </span>
          )}
          {showCountdown && remainingTime <= 30 && (
            <span className="absolute inset-0 border-2 border-red-500 rounded-[6px] animate-pulse pointer-events-none"></span>
          )}
        </button>
      );
    }
    return numbers;
  };

  // Resolve the user's current selection size + matching bulk tier so
  // we can show savings + a "buy N more to unlock 20% off" nudge right
  // here on the number-selection tab. Only what's being bought now counts
  // (picks plus this competition's basket), as in the basket; tickets
  // already bought don't.
  const totalSelected =
    (userSelectedNumbers?.length || 0) +
    (userReservedNumbers?.length || 0);
  const tiers = visibleTiers(bulkPricingTiers);
  const base = parseFloat(ticketPrice) || 0;
  const currentPerTicket = pricePerTicketFor({
    basePrice: base,
    bulkTiers: tiers,
    quantity: totalSelected
  });
  const currentSavedPerTicket = Math.max(0, base - currentPerTicket);
  const isBulkApplied = currentSavedPerTicket > 0 && totalSelected > 0;
  const nextTier = tiers.find(t => t.minQuantity > totalSelected);
  const nextTierPrice = nextTier ? resolveTierPrice(nextTier, base) : null;
  const nextTierPct = nextTier ? Math.round(tierDiscountPercent(nextTier, base)) : 0;
  const ticketsToNextTier = nextTier ? Math.max(0, nextTier.minQuantity - totalSelected) : 0;

  return (
    <div className="space-y-6">
      {/* Bulk savings strip — visible at the top of the number selector
          so customers see the discount opportunity *before* picking
          their tickets, not only when they hit the order summary. */}
      {tiers.length > 0 && (
        <div className="rounded-[8px] border-2 border-success/40 bg-gradient-to-r from-success/10 via-accent/5 to-accent/10 p-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Icon name="ReceiptPercentIcon" size={20} className="text-success" />
              <h4 className="text-base font-bold text-card-foreground">Bulk discounts</h4>
              {isBulkApplied && (
                <span className="text-xs bg-success text-white px-2 py-0.5 rounded-full font-semibold">
                  {Math.round((currentSavedPerTicket / base) * 100)}% off applied
                </span>
              )}
            </div>
            {nextTier && (
              <span className="text-xs text-success font-semibold">
                {ticketsToNextTier > 0
                  ? `Add ${ticketsToNextTier} more to unlock ${nextTierPct}% off`
                  : null}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {tiers.map((tier, idx) => {
              const price = resolveTierPrice(tier, base);
              const pct = Math.round(tierDiscountPercent(tier, base));
              const isActive = totalSelected >= tier.minQuantity;
              return (
                <div
                  key={idx}
                  className={`rounded-[6px] border-2 p-2 transition-all ${
                    isActive
                      ? 'border-success bg-success/10'
                      : 'border-border bg-card'
                  }`}
                >
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wide font-semibold">
                    <span className={isActive ? 'text-success' : 'text-muted-foreground'}>
                      {tier.minQuantity}+ tickets
                    </span>
                    {pct > 0 && (
                      <span className={isActive ? 'text-success' : 'text-foreground'}>
                        -{pct}%
                      </span>
                    )}
                  </div>
                  <div className="text-lg font-bold text-foreground mt-0.5">
                    £{price.toFixed(2)}
                    <span className="text-xs font-normal text-muted-foreground ml-1">/ ticket</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Go straight to a number: the quickest way round a big grid. */}
      <form
        onSubmit={handleSearch}
        className="rounded-[6px] border border-border bg-muted/40 p-3"
      >
        <label htmlFor="go-to-number" className="block text-base font-semibold text-foreground mb-2">
          Go to number
        </label>
        <div className="flex items-center gap-2">
          <input
            id="go-to-number"
            type="number"
            inputMode="numeric"
            value={searchNumber}
            onChange={(e) => setSearchNumber(e?.target?.value)}
            placeholder={`1 to ${totalTickets}`}
            min="1"
            max={totalTickets}
            className="min-w-0 flex-1 h-11 px-3 bg-input text-foreground placeholder-muted-foreground border border-border rounded-[6px] text-base focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
          />
          <button
            type="submit"
            className="h-11 min-w-[44px] px-5 bg-accent text-accent-foreground rounded-[6px] text-base font-semibold hover:bg-accent/90 transition-colors"
          >
            Go
          </button>
        </div>
      </form>

      {renderPager('top')}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-foreground">Select Your Numbers</h3>
          {(userSelectedNumbers?.length > 0 || userReservedNumbers?.length > 0) && (
            <button
              onClick={handleClearAllUserNumbers}
              className="min-h-[44px] px-3 py-1 rounded text-sm font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
            >
              Clear All ({userSelectedNumbers?.length + userReservedNumbers?.length})
            </button>
          )}
        </div>

        {/* Quick Pick — pick N random available numbers in one click. */}
        <div className="flex items-center gap-2 bg-accent/5 border border-accent/20 rounded-[6px] px-3 py-2">
          <span className="text-sm font-medium text-foreground hidden sm:inline">Quick Pick</span>
          <input
            type="number"
            min={1}
            max={50}
            value={quickPickCount}
            onChange={(e) => setQuickPickCount(e?.target?.value)}
            inputMode="numeric"
            className="w-16 h-11 px-2 bg-input text-foreground placeholder-muted-foreground border border-border rounded-[6px] text-base focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="How many random tickets to pick"
          />
          <button
            type="button"
            onClick={handleQuickPick}
            disabled={quickPickBusy}
            className="min-h-[44px] px-3 py-2 rounded-[6px] text-sm font-semibold bg-accent text-accent-foreground hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            title="Pick this many random available numbers for me"
          >
            <span aria-hidden>🎲</span>
            <span>{quickPickBusy ? 'Picking…' : 'Lucky Dip'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2">
        {renderNumbers()}
      </div>

      {renderPager('bottom')}

      <div className="flex flex-wrap items-center gap-4 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 bg-card border-2 border-border rounded"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 bg-yellow-400 border-2 border-yellow-500 rounded shadow-sm ring-1 ring-yellow-300"></div>
          <span className="font-medium">Your Selections</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 bg-orange-400 border-2 border-orange-500 rounded"></div>
          <span>Reserved by Others</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 bg-blue-600 border-2 border-blue-700 rounded shadow-sm ring-1 ring-blue-400"></div>
          <span className="font-medium">Your Purchased Tickets</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 bg-red-600 border-2 border-red-700 rounded"></div>
          <span>Purchased by Others</span>
        </div>
      </div>

      <div className="bg-muted/50 rounded-[6px] p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium">Available: </span>
            <span className="text-green-600">
              {Math.max(
                0,
                totalTickets -
                  (selectedNumbers?.length || 0) -
                  (reservedNumbers?.length || 0) -
                  (userReservedNumbers?.length || 0)
              )}
            </span>
          </div>
          <div>
  <span className="font-medium">Your Numbers: </span>
  <span className="text-accent">
    {userSelectedNumbers?.length 
     + userReservedNumbers?.length 
     + userPurchasedNumbers?.length}
  </span>
</div>

          <div>
            <span className="font-medium">Total Sold: </span>
            <span className="text-muted-foreground">{selectedNumbers?.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

ManualNumberSelector.propTypes = {
  totalTickets: PropTypes?.number?.isRequired,
  selectedNumbers: PropTypes?.array,
  userSelectedNumbers: PropTypes?.array,
  userPurchasedNumbers: PropTypes?.array,
  othersPurchasedNumbers: PropTypes?.array,
  reservedNumbers: PropTypes?.array,
  userReservedNumbers: PropTypes?.array,
  onNumberSelect: PropTypes?.func,
  onNumberDeselect: PropTypes?.func,
  onMultipleNumberSelect: PropTypes?.func,
  onMultipleNumberDeselect: PropTypes?.func
};

export default ManualNumberSelector;